import type {
  Page,
  PortalMeResponse,
  PortalProject,
  PortalRequestType,
  PortalStatus,
  ResolvedForm,
  TenantBrandingResponse,
} from '@servicetim/shared';
import { useQueries, useQuery } from '@tanstack/react-query';
import { type ItemsResponse, portalPath, useApiContext } from './client';
import { pageQuery, usePagedList } from './paging';

/*
 * Portal metadata (`/api/v1`, Bolt 7): what the help center renders itself from. Read-only and
 * slow-changing, so these hooks keep their data for a minute or more; `keys.portal.all` clears
 * the lot (e.g. after an admin edit in the same browser).
 */

/** Help-center default: one request for every project a tenant realistically has (API cap: 200). */
const PORTAL_PROJECTS_PAGE_SIZE = 200;

/** `GET /v1/projects`: projects the caller may view or raise requests in (paged, id order). */
export function usePortalProjects(options: { pageSize?: number } = {}) {
  const { request, keys } = useApiContext();
  const pageSize = options.pageSize ?? PORTAL_PROJECTS_PAGE_SIZE;
  return usePagedList<PortalProject>({
    queryKey: keys.portal.projects({ pageSize }),
    fetchPage: (cursor, signal) =>
      request<Page<PortalProject>>('GET', portalPath('/projects'), {
        query: pageQuery(cursor, pageSize),
        signal,
      }),
    staleTime: 60_000,
  });
}

/** `GET /v1/projects/{id}/request-types`: portal-visible issue types with their `formId` (array). */
export function usePortalRequestTypes(projectId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.portal.requestTypes(projectId ?? ''),
    queryFn: async ({ signal }) =>
      (
        await request<ItemsResponse<PortalRequestType>>(
          'GET',
          portalPath(`/projects/${projectId}/request-types`),
          { signal },
        )
      ).items,
    enabled: projectId !== undefined,
    staleTime: 60_000,
  });
}

/**
 * `GET /v1/projects/{id}/forms/{fid}/resolved`: the portal form as the caller sees it (agent-only
 * fields absent). Feed it to `<SchemaForm form={…} />`; `formId` comes from the request type.
 */
export function usePortalResolvedForm(projectId: string | undefined, formId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.portal.resolvedForm(projectId ?? '', formId ?? ''),
    queryFn: ({ signal }) =>
      request<ResolvedForm>('GET', portalPath(`/projects/${projectId}/forms/${formId}/resolved`), {
        signal,
      }),
    enabled: projectId !== undefined && formId !== undefined,
    staleTime: 60_000,
  });
}

/** `GET /v1/tenant/branding`: tenant name + `settings.branding` (portal name, logo, colors). */
export function useTenantBranding() {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.portal.branding,
    queryFn: ({ signal }) =>
      request<TenantBrandingResponse>('GET', portalPath('/tenant/branding'), { signal }),
    staleTime: 5 * 60_000,
  });
}

/** `GET /v1/me` (portal variant): user, tenant role, organizations, per-project permissions. */
export function usePortalMe() {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.portal.me,
    queryFn: ({ signal }) => request<PortalMeResponse>('GET', portalPath('/me'), { signal }),
    staleTime: 5 * 60_000,
  });
}

/** `GET /v1/projects/{id}/statuses`: live statuses with their customer-facing `portalName`. */
export function usePortalStatuses(projectId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.portal.statuses(projectId ?? ''),
    queryFn: async ({ signal }) =>
      (
        await request<ItemsResponse<PortalStatus>>(
          'GET',
          portalPath(`/projects/${projectId}/statuses`),
          { signal },
        )
      ).items,
    enabled: projectId !== undefined,
    staleTime: 60_000,
  });
}

/** Statuses of several projects at once (one query per project, cached per project). */
export function usePortalStatusesForProjects(projectIds: readonly string[]) {
  const { request, keys } = useApiContext();
  return useQueries({
    queries: projectIds.map((projectId) => ({
      queryKey: keys.portal.statuses(projectId),
      queryFn: async ({ signal }: { signal?: AbortSignal }) =>
        (
          await request<ItemsResponse<PortalStatus>>(
            'GET',
            portalPath(`/projects/${projectId}/statuses`),
            { signal },
          )
        ).items,
      staleTime: 60_000,
    })),
    combine: (results) => ({
      statuses: results.flatMap((r) => r.data ?? []),
      isLoading: results.some((r) => r.isLoading),
    }),
  });
}
